import { format, parseISO } from 'date-fns'

import { EMPTY_LIST } from '../../../../../constants/common'
import { DATE_SHORT_FORMAT } from '../../../../../constants/date'
import { toUkTimezoneDate } from '../../../../../utils/date'
import { getIsMockApplicantId } from '../../../../RentalPortalApplicant'
import { getIsApplicantTerminalStage } from '../RentalPortalApplicantProfileTabOverview.utils'

import {
  APPLICANT_CHECKLIST_CREDIT_REPORT_KEY,
  APPLICANT_CREDIT_REPORT_DOCUMENT_OPTIONS,
  APPLICANT_DOCUMENT_STATUS,
  APPLICANT_DOCUMENT_STATUS_LABEL_KEY,
  APPLICANT_DOCUMENT_STATUS_TEXT_COLOR,
} from './ApplicantDocumentChecklist.constants'

function getApplicantDocumentDate({ date, timezone }) {
  return format(toUkTimezoneDate(parseISO(date), timezone), DATE_SHORT_FORMAT)
}

export function getApplicantChecklistRows(applicantData) {
  return applicantData?.checklist ?? EMPTY_LIST
}

export function getHasApplicantChecklistRows(rows) {
  return rows.length > 0
}

export function getIsApplicantChecklistRowBusy({ busyKey, row }) {
  return busyKey === row.key
}

export function getIsApplicantDocumentMissing(row) {
  return row.status === APPLICANT_DOCUMENT_STATUS.missing
}

export function getIsApplicantDocumentUploaded(row) {
  return row.status === APPLICANT_DOCUMENT_STATUS.uploaded
}

export function getIsApplicantDocumentVerified(row) {
  return row.status === APPLICANT_DOCUMENT_STATUS.verified
}

export function getIsApplicantDocumentRejected(row) {
  return row.status === APPLICANT_DOCUMENT_STATUS.rejected
}

export function getIsApplicantDocumentVerdictLoading({
  isSubmitting,
  status,
  submittingStatus,
}) {
  return isSubmitting && status === submittingStatus
}

export function getApplicantDocumentStatusProps({ row, t }) {
  return {
    text: t(APPLICANT_DOCUMENT_STATUS_LABEL_KEY[row.status] ?? row.status),
    textColor: APPLICANT_DOCUMENT_STATUS_TEXT_COLOR[row.status],
  }
}

export function getApplicantDocumentVerdictMeta({ row, t, timezone }) {
  if (getIsApplicantDocumentVerified(row) && row.verified_by) {
    const date = getApplicantDocumentDate({ date: row.verified_at, timezone })

    return {
      date,
      reason: null,
      tooltip: t('RentalPortal.ApplicantDocumentVerifiedByKey', {
        date,
        name: row.verified_by.fullname,
      }),
    }
  }

  if (getIsApplicantDocumentUploaded(row) && row.file?.uploaded_by) {
    const date = getApplicantDocumentDate({
      date: row.file.uploaded_at,
      timezone,
    })

    return {
      date,
      reason: null,
      tooltip: t('RentalPortal.ApplicantDocumentUploadedByKey', {
        date,
        name: row.file.uploaded_by.fullname,
      }),
    }
  }

  if (!getIsApplicantDocumentRejected(row)) {
    return null
  }

  if (row.rejected_by) {
    const date = getApplicantDocumentDate({ date: row.rejected_at, timezone })

    return {
      date,
      reason: row.rejected_reason ?? null,
      tooltip: t('RentalPortal.ApplicantDocumentRejectedByKey', {
        date,
        name: row.rejected_by.fullname,
      }),
    }
  }

  return row.rejected_reason
    ? { date: null, reason: row.rejected_reason, tooltip: null }
    : null
}

export function getApplicantDocumentVerdictState({
  applicantData,
  isPreview,
  row,
}) {
  const isMissing = getIsApplicantDocumentMissing(row)

  const isUploaded = getIsApplicantDocumentUploaded(row)

  const isVerified = getIsApplicantDocumentVerified(row)

  const isRejected = getIsApplicantDocumentRejected(row)

  const isLocked = isPreview || getIsApplicantTerminalStage(applicantData)

  const hasDocument = isUploaded || isVerified || isRejected

  const tooltipKey = applicantData?.permissions?.can_screen
    ? null
    : 'RentalPortal.ApplicantDocumentVerdictNotAllowedTooltipKey'

  return {
    attachTooltipKey:
      tooltipKey ??
      (applicantData?.directory_id
        ? null
        : 'RentalPortal.ApplicantProfileDocumentsNoFolderKey'),
    // TODO: offer the attach for mock applicants once they have a document folder
    canAttach:
      !isLocked &&
      !getIsMockApplicantId(applicantData?.id) &&
      (isMissing || isRejected),
    canReject: !isLocked && hasDocument && !isRejected,
    canVerify: !isLocked && hasDocument && !isVerified,
    tooltipKey,
  }
}

export function getApplicantDocumentAttachTooltip({ openTooltipText }) {
  return openTooltipText
}

export function getApplicantDocumentTypeLabel({ row, t }) {
  return t(row.type_label)
}

export function getIsApplicantCreditReportRow(row) {
  return row.key === APPLICANT_CHECKLIST_CREDIT_REPORT_KEY
}

export function getApplicantCreditReportDocumentOptions(t) {
  return APPLICANT_CREDIT_REPORT_DOCUMENT_OPTIONS.map(option => ({
    ...option,
    label: t(option.label),
  }))
}

export function toApplicantDocumentPreviewFiles(rows) {
  return rows
    .filter(row => row.file)
    .map(({ file }) => ({ ...file, localUrl: file.url, origin: file.url }))
}
